import React from 'react';
import Icon from '../../../components/AppIcon';

const HowSleeveWorksSection = () => {
  return (
    <section className="py-24 px-4 md:px-6 lg:px-8" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            How Sleeve Works
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            A system-of-systems orchestrator coordinating distributed intelligence across cloud, edge, and human operators.
          </p>
        </div>

        {/* Architecture Diagram */}
        <div className="relative max-w-4xl mx-auto">
          <div className="bg-card/30 border border-border rounded-xl p-8 md:p-12">
            
            {/* Top Layer - Distributed Execution */}
            <div className="mb-8">
              <div className="flex justify-center mb-8">
                <div className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: 'rgba(79, 209, 197, 0.2)', border: '1px solid rgba(79, 209, 197, 0.4)', color: '#4fd1c5' }}>
                  Distributed Execution Layer
                </div>
              </div>
              
              <div className="flex items-start justify-center gap-8 md:gap-12">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 md:w-20 md:h-20 border-2 rounded-lg flex items-center justify-center" style={{ borderColor: '#e6c866', backgroundColor: 'transparent' }}>
                    <Icon name="Cloud" size={28} style={{ color: '#e6c866' }} />
                  </div>
                  <span className="text-sm text-muted-foreground">Cloud Agents</span>
                </div>
                
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 md:w-20 md:h-20 border-2 rounded-lg flex items-center justify-center" style={{ borderColor: '#e6c866', backgroundColor: 'transparent' }}>
                    <Icon name="Cpu" size={28} style={{ color: '#e6c866' }} />
                  </div>
                  <span className="text-sm text-muted-foreground">Edge Agents</span>
                </div>
                
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 md:w-20 md:h-20 border-2 rounded-lg flex items-center justify-center" style={{ borderColor: '#e6c866', backgroundColor: 'transparent' }}>
                    <Icon name="Server" size={28} style={{ color: '#e6c866' }} />
                  </div>
                  <span className="text-sm text-muted-foreground">Legacy Systems</span>
                </div>
              </div>
            </div>

            {/* Arrow Down */}
            <div className="flex justify-center my-6">
              <div className="flex flex-col items-center">
                <div className="w-px h-8" style={{ backgroundColor: '#4fd1c5' }}></div>
                <Icon name="ArrowDown" size={16} style={{ color: '#4fd1c5' }} />
              </div>
            </div>

            {/* Center Layer - Orchestration */}
            <div className="mb-6">
              <div className="border-2 rounded-xl p-6 md:p-8 text-center" style={{ borderColor: 'rgba(79, 209, 197, 0.4)', backgroundColor: 'rgba(79, 209, 197, 0.05)' }}>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-4">
                  Sleeve Orchestration Layer
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Coordinates state, intent, and execution across all participants. Routes events, enforces policies, manages distributed state, and ensures coherent operation—from millisecond edge decisions to strategic cloud analysis.
                </p>
                <div className="flex items-center justify-center gap-8 md:gap-16 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#e6c866' }}></div>
                    <span>Event Router</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#e6c866' }}></div>
                    <span>Policy Engine</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#e6c866' }}></div>
                    <span>State Manager</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Arrow Down */}
            <div className="flex justify-center my-6">
              <div className="flex flex-col items-center">
                <div className="w-px h-8" style={{ backgroundColor: '#4fd1c5' }}></div>
                <Icon name="ArrowDown" size={16} style={{ color: '#4fd1c5' }} />
              </div>
            </div>

            {/* Bottom Layer - Human Oversight */}
            <div>
              <div className="flex justify-center mb-6">
                <div className="inline-flex items-center px-4 py-2 rounded-md text-xs font-semibold uppercase tracking-wider" style={{ backgroundColor: 'rgba(239, 118, 118, 0.2)', border: '1px solid rgba(239, 118, 118, 0.4)', color: '#ef7676' }}>
                  Human Oversight & Control
                </div>
              </div>
              
              <div className="flex justify-center mb-4">
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 md:w-20 md:h-20 border-2 rounded-full flex items-center justify-center" style={{ borderColor: '#e6c866', backgroundColor: 'transparent' }}>
                    <Icon name="Users" size={28} style={{ color: '#e6c866' }} />
                  </div>
                  <span className="text-sm text-muted-foreground">Active Participants</span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground text-center max-w-xl mx-auto">
                Humans participate as active nodes in planning and control—approving critical decisions, adjusting parameters, and maintaining operational oversight.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 max-w-5xl mx-auto">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Icon name="Network" size={32} style={{ color: '#e6c866' }} />
            </div>
            <h4 className="text-lg font-semibold text-foreground mb-2">Decentralized Coordination</h4>
            <p className="text-sm text-muted-foreground">
              No single point of failure. Agents operate autonomously with eventual consistency guarantees.
            </p>
          </div>
          
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Icon name="Layers" size={32} style={{ color: '#4fd1c5' }} />
            </div>
            <h4 className="text-lg font-semibold text-foreground mb-2">Resilient Architecture</h4>
            <p className="text-sm text-muted-foreground">
              Tolerates network partitions, node failures, and degraded connectivity without service disruption.
            </p>
          </div>
          
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <Icon name="Globe" size={32} style={{ color: '#4fd1c5' }} />
            </div>
            <h4 className="text-lg font-semibold text-foreground mb-2">Universal Deployment</h4>
            <p className="text-sm text-muted-foreground">
              Works across air, land, sea, and space domains without architectural modification.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowSleeveWorksSection;